import { DocumentsContext } from './DocumentsContext';
import React, { useState } from 'react';
import { GearSelectedDocument } from '../../Model/GearSelectedDocument';
import { buildDocumentEndpointPath } from './DocumentsService';
import { API_URL, CONTENT_TYPE_FIELD, DOC_ID_FIELD } from '../../config/consts';
import { useCurrentUserContext } from '../currentUser/CurrentUserContext';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { v4 as uuidv4 } from 'uuid';
import { GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT } from '../../Model/enums/GearUserAllowedActions';
import { useDownloadMany } from './useDownloadMany';
import { GEAR_NOTIFICATION_TYPE } from '../../Model/enums/GearNotificationType';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';

interface DocumentsProviderProps {
  children?: React.ReactNode;
}

export const DocumentsProvider: React.FC<DocumentsProviderProps> = ({ children }) => {
  const [selectedDocuments, setSelectedDocuments] = useState<GearSelectedDocument[]>([]);
  const [openDocumentLoading, setOpenDocumentLoading] = useState<boolean>(false);
  const { addCenterNotification, showToast } = useNotificationContext();
  const { selectedIndex } = useCurrentUserContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { mutate: downloadManyMutation } = useDownloadMany();

  const openDocument = async (contentType: string, documentId: string) => {
    setOpenDocumentLoading(true);
    const redirectDocumentUrl = `${API_URL}/${buildDocumentEndpointPath(contentType, documentId, selectedIndex?.id, true)}`;
    window.open(redirectDocumentUrl);
    setOpenDocumentLoading(false);
  };

  const downloadSingleFile = async () => {
    if (
      selectedDocuments[0] &&
      selectedDocuments[0].Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.DOWNLOAD)
    ) {
      const downloadUrl = `${API_URL}/${buildDocumentEndpointPath(
        selectedDocuments[0][CONTENT_TYPE_FIELD],
        selectedDocuments[0][DOC_ID_FIELD],
        selectedIndex?.id,
        false
      )}`;

      window.open(downloadUrl, '_blank');
    }
  };

  const downloadFiles = async () => {
    switch (selectedDocuments.length) {
      case 0:
        break;
      case 1:
        await downloadSingleFile();

        addCenterNotification({
          id: uuidv4(),
          title: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentStartedTitle'),
          message: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentStartedMessage'),
          timestamp: new Date(),
          type: 'success'
        });

        showToast({
          type: GEAR_NOTIFICATION_TYPE.SUCCESS,
          message: translate('custom.notifications.notificationCenter.downloadDocument.downloadDocumentStartedTitle')
        });
        break;
      default: {
        const downloadableDocuments = selectedDocuments.filter(doc =>
          doc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.DOWNLOAD)
        );

        if (downloadableDocuments.length > 0 && selectedIndex?.id !== undefined) {
          const dto = {
            documents: downloadableDocuments.map(doc => ({
              docId: doc[DOC_ID_FIELD],
              contentType: doc[CONTENT_TYPE_FIELD]
            })),
            indexId: selectedIndex?.id
          };

          downloadManyMutation({ dto });
        }
        break;
      }
    }
  };

  return (
    <DocumentsContext.Provider
      value={{
        openDocumentLoading,
        selectedDocuments,
        setSelectedDocuments,
        openDocument,
        downloadFiles
      }}
    >
      {children}
    </DocumentsContext.Provider>
  );
};
